import React, { useState, useEffect } from "react";
import {
  TextField,
  Checkbox,
  RadioGroup,
  Radio,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
} from "@mui/material";
import { SearchIcon, CircleX } from "lucide-react";
import BrandCheckbox from "../BrandCheckbox";
import {
  useLazyFetchVendorStoreQuery,
  useFetchAllBrandsQuery,
} from "../../../../../services/baseQuery";

const CustomerSettingsTab = ({
  customerData,
  vcConfigSettings,
  onUpdate,
  isUpdating,
}) => {
  const [
    useDefaultOrderValidationSettings,
    setUseDefaultOrderValidationSettings,
  ] = useState(
    vcConfigSettings?.settings?.orderValidation?.validationType ===
      "COMPANY_SETTINGS",
  );

  const [orderValidation, setOrderValidation] = useState({
    validationType:
      vcConfigSettings?.settings?.orderValidation?.validationType ||
      "USER_SPECIFIC",
    minOrderQty: vcConfigSettings?.settings?.orderValidation?.minOrderQty || "",
    minOrderValue:
      vcConfigSettings?.settings?.orderValidation?.minOrderValue || "",
    validationCondition:
      vcConfigSettings?.settings?.orderValidation?.validationCondition ||
      "ANY_ONE",
  });

  const [creditLimit, setCreditLimit] = useState(
    vcConfigSettings?.settings?.creditLimit || "",
  );
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectAllBrands, setSelectAllBrands] = useState(
    vcConfigSettings?.settings?.brandVisibility?.visibilityType ===
      "ALL_COMPANY_BRANDS",
  );
  const [brandFilter, setBrandFilter] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  const [notes, setNotes] = useState(vcConfigSettings?.additionalNotes || "");
  const [hasChanges, setHasChanges] = useState(false);

  // Queries
  const [triggerFetchVendorStore, { data: vendorStoreData }] =
    useLazyFetchVendorStoreQuery();
  const { data: brandsResponseData, isLoading: fetchBrandsLoading } =
    useFetchAllBrandsQuery();

  useEffect(() => {
    if (useDefaultOrderValidationSettings) {
      triggerFetchVendorStore();
    }
  }, [useDefaultOrderValidationSettings]);

  useEffect(() => {
    if (vendorStoreData?.success && useDefaultOrderValidationSettings) {
      const { minOrderQty, minOrderValue, validationCondition } =
        vendorStoreData.data.store.orderValidation;
      setOrderValidation(prev => ({
        ...prev,
        validationType: "COMPANY_SETTINGS",
        minOrderQty: minOrderQty || "",
        minOrderValue: minOrderValue || "",
        validationCondition: validationCondition || "ANY_ONE",
      }));
    }
  }, [vendorStoreData, useDefaultOrderValidationSettings]);

  useEffect(() => {
    if (vcConfigSettings?.settings?.brandVisibility) {
      const { visibilityType, removedBrands } =
        vcConfigSettings.settings.brandVisibility;

      if (visibilityType === "ALL_COMPANY_BRANDS") {
        setSelectAllBrands(true);
        if (brandsResponseData?.data?.brands) {
          setSelectedBrands(
            brandsResponseData.data.brands.map(brand => brand._id),
          );
        }
      } else {
        setSelectAllBrands(false);
        if (brandsResponseData?.data?.brands) {
          const removedBrandIds = removedBrands.map(brand => brand._id);
          const selectedIds = brandsResponseData.data.brands
            .filter(brand => !removedBrandIds.includes(brand._id))
            .map(brand => brand._id);
          setSelectedBrands(selectedIds);
        }
      }
    }
  }, [vcConfigSettings, brandsResponseData]);

  useEffect(() => {
    const hasCreditsChanged =
      Number(creditLimit) !== vcConfigSettings?.settings?.creditLimit;

    const hasOrderValidationChanged =
      useDefaultOrderValidationSettings !==
        (vcConfigSettings?.settings?.orderValidation?.validationType ===
          "COMPANY_SETTINGS") ||
      (!useDefaultOrderValidationSettings &&
        (Number(orderValidation.minOrderQty) !==
          vcConfigSettings?.settings?.orderValidation?.minOrderQty ||
          Number(orderValidation.minOrderValue) !==
            vcConfigSettings?.settings?.orderValidation?.minOrderValue ||
          orderValidation.validationCondition !==
            vcConfigSettings?.settings?.orderValidation?.validationCondition));

    const hasBrandVisibilityChanged =
      selectAllBrands !==
      (vcConfigSettings?.settings?.brandVisibility?.visibilityType ===
        "ALL_COMPANY_BRANDS");

    const hasNotesChanged = notes !== vcConfigSettings?.additionalNotes;

    setHasChanges(
      hasCreditsChanged ||
        hasOrderValidationChanged ||
        hasBrandVisibilityChanged ||
        hasNotesChanged,
    );
  }, [creditLimit, orderValidation, selectAllBrands, notes, vcConfigSettings]);

  // Brand handling functions
  const handleBrandSelection = brandId => {
    const allBrands = brandsResponseData?.data?.brands || [];

    setSelectedBrands(prev => {
      const newSelection = prev.includes(brandId)
        ? prev.filter(id => id !== brandId)
        : [...prev, brandId];

      if (newSelection.length === allBrands.length) {
        setSelectAllBrands(true);
      } else {
        setSelectAllBrands(false);
      }

      return newSelection;
    });
  };

  const handleSelectAllBrands = event => {
    const checked = event.target.checked;
    setSelectAllBrands(checked);

    if (checked) {
      const allBrandIds =
        brandsResponseData?.data?.brands?.map(brand => brand._id) || [];
      setSelectedBrands(allBrandIds);
    } else {
      setSelectedBrands([]);
    }
  };

  const getFilteredBrands = () => {
    if (!brandsResponseData?.success) return { brands: [], isEmpty: true };

    const allBrands = brandsResponseData.data.brands;
    const searchFiltered = searchQuery
      ? allBrands.filter(brand =>
          brand.name.toLowerCase().includes(searchQuery.toLowerCase()),
        )
      : allBrands;

    const filteredBrands =
      {
        SELECTED: searchFiltered.filter(brand =>
          selectedBrands.includes(brand._id),
        ),
        UNSELECTED: searchFiltered.filter(
          brand => !selectedBrands.includes(brand._id),
        ),
        ALL: searchFiltered,
      }[brandFilter] || searchFiltered;

    return {
      brands: filteredBrands,
      isEmpty: filteredBrands.length === 0,
    };
  };

  return (
    <div className="ml-[2.083333vw] mr-[2.638888vw] space-y-6">
      {/* Credit Limit Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Credit Settings</h3>
        </div>
        <div className="p-6">
          <div className="max-w-md">
            <TextField
              label="Credit Limit"
              variant="outlined"
              size="small"
              type="number"
              value={creditLimit}
              onChange={e => setCreditLimit(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">₹</InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  "&:hover fieldset": {
                    borderColor: "#5652B0",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#5652B0",
                  },
                },
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#5652B0",
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Order Validation Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Order Validation
          </h3>
        </div>
        <div className="p-6 space-y-6">
          <div className="flex items-center">
            <Checkbox
              checked={useDefaultOrderValidationSettings}
              onChange={e =>
                setUseDefaultOrderValidationSettings(e.target.checked)
              }
              size="small"
              sx={{
                color: "#5652B0",
                "&.Mui-checked": { color: "#5652B0" },
              }}
            />
            <span className="text-sm">Use company default settings</span>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <RadioGroup
                value={orderValidation.validationCondition}
                onChange={e =>
                  setOrderValidation(prev => ({
                    ...prev,
                    validationCondition: e.target.value,
                  }))
                }
                row
                size="small"
                disabled={useDefaultOrderValidationSettings}>
                <FormControlLabel
                  value="ANY_ONE"
                  control={
                    <Radio
                      size="small"
                      disabled={useDefaultOrderValidationSettings}
                      sx={{
                        color: "#5652B0",
                        "&.Mui-checked": { color: "#5652B0" },
                      }}
                    />
                  }
                  label={<span className="text-sm">Any One</span>}
                />
                <FormControlLabel
                  value="BOTH"
                  control={
                    <Radio
                      size="small"
                      disabled={useDefaultOrderValidationSettings}
                      sx={{
                        color: "#5652B0",
                        "&.Mui-checked": { color: "#5652B0" },
                      }}
                    />
                  }
                  label={<span className="text-sm">Both</span>}
                />
              </RadioGroup>
            </div>

            <div className="grid grid-cols-2 gap-4 max-w-md">
              <TextField
                label="Minimum Order Quantity"
                variant="outlined"
                size="small"
                type="number"
                value={
                  useDefaultOrderValidationSettings
                    ? vendorStoreData?.data?.store?.orderValidation?.minOrderQty
                    : orderValidation.minOrderQty
                }
                onChange={e =>
                  setOrderValidation(prev => ({
                    ...prev,
                    minOrderQty: e.target.value,
                  }))
                }
                disabled={useDefaultOrderValidationSettings}
                fullWidth
              />
              <TextField
                label="Minimum Order Value"
                variant="outlined"
                size="small"
                type="number"
                value={
                  useDefaultOrderValidationSettings
                    ? vendorStoreData?.data?.store?.orderValidation
                        ?.minOrderValue
                    : orderValidation.minOrderValue
                }
                onChange={e =>
                  setOrderValidation(prev => ({
                    ...prev,
                    minOrderValue: e.target.value,
                  }))
                }
                disabled={useDefaultOrderValidationSettings}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">₹</InputAdornment>
                  ),
                }}
                fullWidth
              />
            </div>
          </div>
        </div>
      </div>

      {/* Brand Visibility Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Brand Visibility
          </h3>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-x-4">
              <div className="flex items-center">
                <Checkbox
                  checked={selectAllBrands}
                  onChange={handleSelectAllBrands}
                  sx={{
                    color: "#5652B0",
                    "&.Mui-checked": { color: "#5652B0" },
                  }}
                />
                <span className="text-sm font-medium">Select all brands</span>
              </div>
            </div>

            <div className="flex items-center gap-x-4">
              <div className="flex gap-x-3">
                {["ALL", "SELECTED", "UNSELECTED"].map(filter => (
                  <button
                    key={filter}
                    onClick={() => setBrandFilter(filter)}
                    className={`text-sm font-medium transition-colors ${
                      brandFilter === filter
                        ? "text-[#5652B0] underline"
                        : "text-gray-600 hover:text-[#5652B0]"
                    }`}>
                    View {filter.toLowerCase()}
                  </button>
                ))}
              </div>

              <div className="relative">
                <SearchIcon
                  className="absolute left-3 top-1/2 transform -translate-y-1/2"
                  size={16}
                  color="#83899F"
                />
                <input
                  type="text"
                  placeholder="Search brands..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-[200px] h-9 pl-9 pr-8 rounded-lg border text-sm focus:border-[#5652B0] focus:ring-1 focus:ring-[#5652B0] outline-none"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full">
                    <CircleX size={14} color="#83899F" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-4 gap-3 max-h-[400px] overflow-y-auto p-3 bg-gray-50 rounded-lg">
            {fetchBrandsLoading ? (
              <div className="col-span-4 flex items-center justify-center h-[100px]">
                <CircularProgress size={32} sx={{ color: "#5652B0" }} />
              </div>
            ) : getFilteredBrands().isEmpty ? (
              <div className="col-span-4 flex items-center justify-center h-[100px] text-gray-500">
                No brands found
              </div>
            ) : (
              getFilteredBrands().brands.map(brand => (
                <BrandCheckbox
                  key={brand._id}
                  id={brand._id}
                  label={brand.name}
                  checked={selectedBrands.includes(brand._id)}
                  onChange={() => handleBrandSelection(brand._id)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Additional Notes Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Additional Notes
          </h3>
        </div>
        <div className="p-6">
          <TextField
            multiline
            rows={3}
            placeholder="Add any additional notes or comments..."
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                "&:hover fieldset": {
                  borderColor: "#5652B0",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#5652B0",
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#5652B0",
              },
            }}
          />
        </div>
      </div>

      {/* Save Changes Button */}
      <div className="flex justify-end">
        <button
          onClick={() =>
            onUpdate({
              creditLimit: Number(creditLimit),
              orderValidation: {
                validationType: useDefaultOrderValidationSettings
                  ? "COMPANY_SETTINGS"
                  : "USER_SPECIFIC",
                ...(useDefaultOrderValidationSettings
                  ? {}
                  : {
                      minOrderQty: Number(orderValidation.minOrderQty),
                      minOrderValue: Number(orderValidation.minOrderValue),
                      validationCondition: orderValidation.validationCondition,
                    }),
              },
              brandVisibility: {
                visibilityType: selectAllBrands
                  ? "ALL_COMPANY_BRANDS"
                  : "USER_SPECIFIC",
                removedBrands: selectAllBrands
                  ? []
                  : brandsResponseData?.data?.brands
                      .filter(brand => !selectedBrands.includes(brand._id))
                      .map(brand => brand._id),
              },
              additionalNotes: notes,
            })
          }
          disabled={!hasChanges || isUpdating}
          className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2
            ${
              hasChanges && !isUpdating
                ? "bg-[#5652B0] text-white hover:bg-[#4A47A3]"
                : "bg-gray-100 text-gray-400 cursor-not-allowed"
            }`}>
          {isUpdating ? (
            <>
              <CircularProgress size={16} sx={{ color: "inherit" }} />
              <span>Saving...</span>
            </>
          ) : (
            "Save Changes"
          )}
        </button>
      </div>
    </div>
  );
};

export default CustomerSettingsTab;
